import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
    FaWhatsapp,
    FaEnvelope,
    FaIndustry,
    FaTools,
    FaLeaf,
    FaUtensils,
    FaLaptopCode,
    FaShoppingBag,
    FaMapMarkerAlt,
    FaGlobe,
    FaUser // Added for Owner
} from 'react-icons/fa';

// --- Styled Components ---

const Card = styled(motion.div)`
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    height: 100%;
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
        transform: translateY(-5px);
    }
`;

const CardImage = styled.div`
    height: 200px;
    background-image: url(${props => props.$src});
    background-size: cover;
    background-position: center;
`;

const CardContent = styled.div`
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
`;

const CardHeader = styled.div`
    margin-bottom: 1rem;
    border-bottom: 1px solid #f0f0f0;
    padding-bottom: 0.5rem;
`;

const CardTitle = styled.h3`
    font-size: 1.4rem;
    margin: 0 0 1rem 0;
    color: #2d3436;
    font-family: 'Playfair Display', serif;
    font-weight: 700;
`;

const MetaInfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const InfoRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 0.9rem;
    color: #636e72;
    line-height: 1.4;

    svg {
        flex-shrink: 0;
        margin-top: 3px;
        color: #6c5ce7; /* Unified icon color for meta info */
        width: 16px;
    }

    span, a {
        word-break: break-word;
    }

    a {
        color: #636e72;
        text-decoration: none;
        transition: color 0.2s;
        &:hover {
            color: #6c5ce7;
            text-decoration: underline;
        }
    }
`;

const DescriptionRow = styled.div`
    display: flex;
    gap: 12px;
    margin-top: 1rem;
    margin-bottom: 1.5rem;
    flex-grow: 1;

    svg {
        flex-shrink: 0;
        color: #b2bec3; /* Different color for industry icon to distinguish it */
        font-size: 1.2rem;
        margin-top: 4px;
    }

    p {
        font-size: 0.95rem;
        color: #555;
        line-height: 1.6;
        margin: 0;
    }
`;

const ContactActions = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-top: auto;
    padding-top: 1rem;
    border-top: 1px solid #eee;
`;

const ActionLink = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 600;
    padding: 10px 12px;
    border-radius: 6px;
    transition: background 0.2s;
    flex: 1;

    &.email {
        color: #0984e3;
        background: #e6f2ff;
        &:hover { background: #d1e8ff; }
    }

    &.whatsapp {
        color: #00b894;
        background: #e3fdf5;
        &:hover { background: #cbfbf0; }
    }
`;

// --- Helper Functions ---

const getIndustryIcon = (description) => {return <FaTools />;
};

const BusinessCard = ({ business, onClick }) => {

    const getWhatsAppUrl = (mobile) => {
        if (!mobile) return '#';
        const cleanNumber = mobile.replace(/[^0-9]/g, '');
        return `https://wa.me/${cleanNumber}`;
    };

    return (
        <Card onClick={onClick} whileTap={{ scale: 0.98 }}>
            <CardImage $src={business.image || '/assets/images/placeholder.jpg'} />

            <CardContent>
                <CardHeader>
                    <CardTitle>{business.name}</CardTitle>

                    <MetaInfoContainer>
                        {/* Owner with Icon */}
                        <InfoRow>
                            <FaUser title="Owner" />
                            <span>{business.owner}</span>
                        </InfoRow>

                        {/* Address with Icon */}
                        {business.address && (
                            <InfoRow>
                                <FaMapMarkerAlt title="Address" />
                                <span>{business.address}</span>
                            </InfoRow>
                        )}

                        {/* Website with Icon */}
                        {business.website && (
                            <InfoRow>
                                <FaGlobe title="Website" />
                                <a
                                    href={business.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {business.website}
                                </a>
                            </InfoRow>
                        )}
                    </MetaInfoContainer>
                </CardHeader>

                {/* Description with Industry Icon */}
                <DescriptionRow>
                    {getIndustryIcon(business.description)}
                    <p>{business.description ? `${business.description.substring(0, 100)}...` : 'No description available.'}</p>
                </DescriptionRow>

                {/* Contact Actions: Email & WhatsApp */}
                <ContactActions onClick={(e) => e.stopPropagation()}>
                    {business.contact && (
                        <ActionLink
                            href={`mailto:${business.contact}`}
                            className="email"
                            title="Send Email"
                        >
                            <FaEnvelope /> Email
                        </ActionLink>
                    )}

                    {business.mobile && (
                        <ActionLink
                            href={getWhatsAppUrl(business.mobile)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="whatsapp"
                            title="Chat on WhatsApp"
                        >
                            <FaWhatsapp /> WhatsApp
                        </ActionLink>
                    )}
                </ContactActions>
            </CardContent>
        </Card>
    );
};

export default BusinessCard;